import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";
import WorkflowBadge from "./WorkflowBadge";
import ListingDetailsModal from "./ListingDetailsModal";
import TradeDetailsModal from "./TradeDetailsModal";

const SuperUserDraftReview = () => {
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState(null);

  const loadDrafts = async () => {
    setLoading(true);
    try {
      const [listingRes, tradeRes] = await Promise.all([
        axiosInstance.get("/listings", { params: { drafts: 1 } }),
        axiosInstance.get("/trades", { params: { drafts: 1 } }),
      ]);
      setListings(listingRes.data || []);
      setTrades(tradeRes.data || []);
    } catch (_) {
      setListings([]);
      setTrades([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-blue-900 mb-2">
          Review Agent Drafts
        </h1>
        <p className="text-gray-600 mb-6">
          Open a draft, correct the details and conditions, then save. After you
          save, the agent can no longer edit that record. You will get a
          notification when the compliance review is ready; it is also under Trade
          Info → Compliance Review and Listing Info → Compliance Review.
        </p>

        <div className="flex gap-4 mb-6 border-b">
          <button
            className={`px-4 py-2 font-medium ${
              tab === "listings"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500"
            }`}
            onClick={() => setTab("listings")}
          >
            Listing drafts ({listings.length})
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              tab === "trades"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500"
            }`}
            onClick={() => setTab("trades")}
          >
            Trade drafts ({trades.length})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900"></div>
          </div>
        ) : tab === "listings" ? (
          listings.length === 0 ? (
            <p className="text-gray-500">No listing drafts waiting for review.</p>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-blue-900 text-white">
                    <th className="text-left px-4 py-3">Listing #</th>
                    <th className="text-left px-4 py-3">Address</th>
                    <th className="text-left px-4 py-3">MLS #</th>
                    <th className="text-left px-4 py-3">Agent</th>
                    <th className="text-left px-4 py-3">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((listing) => (
                    <tr
                      key={listing._id}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedListing(listing)}
                    >
                      <td className="px-4 py-3">#{listing.listingNumber}</td>
                      <td className="px-4 py-3">
                        {[
                          listing.address?.streetNumber,
                          listing.address?.streetName,
                          listing.address?.city,
                        ]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </td>
                      <td className="px-4 py-3">{listing.mlsNumber || "—"}</td>
                      <td className="px-4 py-3">
                        {listing.createdByEmail ||
                          listing.agent?.employeeNo ||
                          "—"}
                      </td>
                      <td className="px-4 py-3">
                        <WorkflowBadge record={listing} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : trades.length === 0 ? (
          <p className="text-gray-500">No trade drafts waiting for review.</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-blue-900 text-white">
                  <th className="text-left px-4 py-3">Trade #</th>
                  <th className="text-left px-4 py-3">Address</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Agent</th>
                  <th className="text-left px-4 py-3">Review</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade) => (
                  <tr
                    key={trade._id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedTrade(trade)}
                  >
                    <td className="px-4 py-3">#{trade.tradeNumber}</td>
                    <td className="px-4 py-3">
                      {[
                        trade.keyInfo?.streetNumber,
                        trade.keyInfo?.streetName,
                        trade.keyInfo?.city,
                      ]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {trade.keyInfo?.status || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {trade.agentCommissionList?.[0]?.agentName ||
                        trade.createdByEmail ||
                        "—"}
                    </td>
                    <td className="px-4 py-3">
                      <WorkflowBadge record={trade} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedListing && (
        <ListingDetailsModal
          listing={selectedListing}
          onClose={() => setSelectedListing(null)}
          onListingUpdated={loadDrafts}
        />
      )}
      {selectedTrade && (
        <TradeDetailsModal
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
          onUpdate={() => {
            setSelectedTrade(null);
            loadDrafts();
          }}
        />
      )}
    </div>
  );
};

export default SuperUserDraftReview;
